import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, TrendingUp, TrendingDown, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { metricTile } from '@/lib/metricTileStyles';
import InvestmentGradeAnalytics from './InvestmentGradeAnalytics';

interface TrendAnalysis {
  year: number;
  occupation_code: string;
  area_code: string;
  growth_5yr: number;
  growth_10yr: number;
  volatility_index: number;
  recession_impact_score: number;
  peak_year: number;
  trough_year: number;
  recovery_periods: number;
}

interface VolatilityDashboardProps {
  trendAnalysis: TrendAnalysis;
  wageHistory: Array<{
    year: number;
    a_mean: number;
    employment: number;
  }>;
  occupationTitle?: string;
  className?: string;
}

export default function VolatilityDashboard({
  trendAnalysis,
  wageHistory,
  occupationTitle = "this occupation",
  className = ""
}: VolatilityDashboardProps) {
  // Calculate volatility level and color
  const getVolatilityLevel = (volatilityIndex: number) => {
    if (volatilityIndex < 0.05) return { level: 'Very Low', color: metricTile.green.icon, bgColor: metricTile.green.surface };
    if (volatilityIndex < 0.10) return { level: 'Low', color: metricTile.green.icon, bgColor: metricTile.green.surface };
    if (volatilityIndex < 0.20) return { level: 'Moderate', color: metricTile.yellow.icon, bgColor: metricTile.yellow.surface };
    if (volatilityIndex < 0.30) return { level: 'High', color: metricTile.orange.icon, bgColor: metricTile.orange.surface };
    return { level: 'Very High', color: metricTile.red.icon, bgColor: metricTile.red.surface };
  };

  const getRecessionImpactLevel = (impactScore: number) => {
    if (impactScore > -0.02) return { level: 'Resilient', color: metricTile.green.icon };
    if (impactScore > -0.05) return { level: 'Minor Impact', color: metricTile.yellow.icon };
    if (impactScore > -0.10) return { level: 'Moderate Impact', color: metricTile.orange.icon };
    return { level: 'High Impact', color: metricTile.red.icon };
  };

  const volatilityLevel = getVolatilityLevel(trendAnalysis.volatility_index || 0);
  const recessionImpact = getRecessionImpactLevel(trendAnalysis.recession_impact_score || 0);

  // Prepare volatility chart data
  const volatilityData = wageHistory.map((item, index) => {
    const nextItem = wageHistory[index + 1];
    const yoyGrowth = nextItem ? ((nextItem.a_mean - item.a_mean) / item.a_mean) : 0;
    
    return {
      year: item.year,
      yoyGrowth: yoyGrowth * 100, // Convert to percentage
      salary: item.a_mean,
      employment: item.employment
    };
  });

  const formatPercentage = (value: number) => `${(value * 100).toFixed(1)}%`;
  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Investment-Grade Analytics */}
      <InvestmentGradeAnalytics
        trendAnalysis={trendAnalysis}
        occupationTitle={occupationTitle}
      />
      
      {/* Market Stability Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Market Stability Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Volatility Index */}
            <div className={`p-4 rounded-lg ${volatilityLevel.bgColor}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Volatility Index</span>
                <Badge variant="outline" className={volatilityLevel.color}>
                  {volatilityLevel.level}
                </Badge>
              </div>
              <div className="text-2xl font-bold mb-1 text-foreground">
                {formatPercentage(trendAnalysis.volatility_index || 0)}
              </div>
              <Progress 
                value={(trendAnalysis.volatility_index || 0) * 500} // Scale for display
                className="h-2"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Standard deviation of year-over-year growth
              </p>
            </div>

            {/* Recession Impact */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Recession Resilience</span>
                <AlertTriangle className={`h-4 w-4 ${recessionImpact.color}`} />
              </div>
              <div className="text-2xl font-bold mb-1 text-foreground">
                {formatPercentage(trendAnalysis.recession_impact_score || 0)}
              </div>
              <Badge variant="outline" className={recessionImpact.color}>
                {recessionImpact.level}
              </Badge>
              <p className="text-xs text-muted-foreground mt-2">
                Average impact during recession periods
              </p>
            </div>

            {/* Recovery Periods */}
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Recovery Track Record</span>
                <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-2xl font-bold mb-1 text-foreground">
                {trendAnalysis.recovery_periods || 0}
              </div>
              <div className="text-sm text-muted-foreground">
                Recovery cycles completed
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Number of successful recoveries from downturns
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Growth Volatility Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Growth Volatility Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={volatilityData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="year" 
                  className="text-sm"
                />
                <YAxis 
                  tickFormatter={(value) => `${value}%`}
                  className="text-sm"
                />
                <Tooltip
                  formatter={(value, name) => [
                    `${Number(value).toFixed(1)}%`,
                    name === 'yoyGrowth' ? 'Year-over-Year Growth' : name
                  ]}
                  labelFormatter={(label) => `Year: ${label}`}
                />
                
                {/* Zero line for reference */}
                <Line
                  y={0}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="2 2"
                />
                
                <Area
                  type="monotone"
                  dataKey="yoyGrowth"
                  stroke="hsl(var(--accent))"
                  fill="hsl(var(--accent))"
                  fillOpacity={0.1}
                  strokeWidth={2}
                  name="Year-over-Year Growth"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            Shows annual salary growth rate variations. Higher volatility indicates less predictable career progression.
          </div>
        </CardContent>
      </Card>

      {/* Peak and Trough Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Historical Performance Peaks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`p-4 ${metricTile.green.surface}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className={`h-4 w-4 ${metricTile.green.icon}`} />
                <span className="font-medium text-foreground">Peak Performance Year</span>
              </div>
              <div className={`text-2xl font-bold ${metricTile.green.value}`}>
                {trendAnalysis.peak_year || 'N/A'}
              </div>
              <p className={`text-sm mt-1 ${metricTile.green.label}`}>
                Highest employment/salary growth period
              </p>
            </div>

            <div className={`p-4 ${metricTile.red.surface}`}>
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className={`h-4 w-4 ${metricTile.red.icon}`} />
                <span className="font-medium text-foreground">Trough Performance Year</span>
              </div>
              <div className={`text-2xl font-bold ${metricTile.red.value}`}>
                {trendAnalysis.trough_year || 'N/A'}
              </div>
              <p className={`text-sm mt-1 ${metricTile.red.label}`}>
                Most challenging period for the occupation
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-muted/30 rounded-lg">
            <h4 className="font-medium mb-2">Stability Assessment</h4>
            <p className="text-sm text-muted-foreground">
              Based on {wageHistory.length} years of data, this occupation shows{' '}
              <span className={volatilityLevel.color}>{volatilityLevel.level.toLowerCase()}</span> volatility 
              and has demonstrated {recessionImpact.level.toLowerCase()} during economic downturns. 
              {trendAnalysis.recovery_periods > 0 && (
                ` The occupation has successfully recovered ${trendAnalysis.recovery_periods} time${trendAnalysis.recovery_periods !== 1 ? 's' : ''} from major setbacks.`
              )}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}